import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

// styles
import './style.scss';

// antd component
import { Form, Radio, Button, Input } from 'antd';

// containers
import SelectFieldAxios from 'containers/SelectFieldAxios';

// components
import CustomDate from 'components/Forms/Fields/CustomDate';
import CustomSelect from 'components/Forms/Fields/CustomSelect';

// utils
import { DAY_OF_WEEK } from 'utils/helpers/helper.constant';
import { clean, getArrayIds, getObjId } from 'utils/helpers/helper.general';
import { getStatusesList  } from 'utils/helpers/helper.generateData';
// hooks
import { useRefState } from 'utils/hooks';

// global user
import { useChangeChildByID, useGetLocalUser } from 'engine/Contexts/User.context';

function SicknessForm({
  formData,
  // actions
  nextStep,
  prevStep,
  result,
  selectedStudent,
  setSelectedStudent,
  // default
  className,
}) {
  const childClassName = classNames('form-absence', className);

  const localUser = useGetLocalUser()();
  const changeChildByID = useChangeChildByID();

  const now = moment(formData?.date) || moment();
  const dateFormat = 'DD.MM.YYYY';
  let initFormData = {
    duration: 'day',
    status: 'sick',
    date: moment(now, dateFormat)
  };

  const [isDisabled, setIsDisabled] = useState(false);
  const [durationType, setDurationType] = useState(initFormData?.duration);

  const [absenceStatus, setAbsenceStatus] = useState(
    formData?.status || initFormData?.status,
  );

  const [endponitObj, setEndponitObj] = useState(false);
  const [currentDay, setCurrentDay] = useState(now);

  const [hasCourse, setHasCourse] = useState(false);
  const [currentCourse, currentCourseRef, setCurrentCourse] = useRefState([]);

  // only for teacher mode
  const [studentParent, studentParentRef, setStudentParent] = useRefState(false);

  const [AbsFormSickness] = Form.useForm();

  /* 
   * Check current form data from parent
   * Set old data to the form
   */
  useEffect(() => {
    
    if (formData) {
      const hasValFormData = clean(formData);

      setIsDisabled(false);
      AbsFormSickness.setFieldsValue({
        type: hasValFormData?.type,
        duration: hasValFormData?.duration || initFormData?.duration,
        status: hasValFormData?.status || initFormData?.status,
        student: hasValFormData?.student?._id || hasValFormData?.student,
      })

      setAbsenceStatus(hasValFormData?.status || initFormData?.status);

      if (
        hasValFormData.hasOwnProperty('courses') &&
        hasValFormData.courses.length > 0
      ) {
        AbsFormSickness.setFieldsValue({
          courses: getArrayIds(hasValFormData.courses),
        });
      }
      
      if ( hasValFormData.hasOwnProperty('duration') ) {
        setDurationType(hasValFormData?.duration)
      }

      if (hasValFormData.hasOwnProperty('date')) {
        setCurrentDay(moment(hasValFormData.date));
        setEndponitObj({
          course_week:
            DAY_OF_WEEK[moment(hasValFormData.date).isoWeekday() - 1]?.value,
        });
      } else {
        setCurrentDay(now);
        setEndponitObj({
          course_week: DAY_OF_WEEK[now.isoWeekday() - 1]?.value,
        });
      }

      if (hasValFormData?.student?._id) {
        setSelectedStudent(hasValFormData?.student);
        localUser?.role == 'parent' && (
          changeChildByID(hasValFormData?.student?._id || hasValFormData?.student)
        )
        const contact_person = hasValFormData?.student?.contact_person?._id || hasValFormData?.student?.contact_person;
        const parent = contact_person || hasValFormData?.student?.parents[0]?._id || hasValFormData?.student?.parents[0]
        setStudentParent(parent);
      }

    } else {
      setCurrentDay(now);
      setEndponitObj({
        course_week: DAY_OF_WEEK[now.isoWeekday() - 1]?.value,
      });
    }
    
  }, [formData]);

  /* 
   * Check current date and day of the week 
   * for generate Courses list
   */
  useEffect(() => {
    if (currentDay) {
      // 1 - Monday, 7 - Sunday
      const week = currentDay.isoWeekday();
      setEndponitObj({
        course_week: DAY_OF_WEEK[week - 1]?.value,
      }); 
    } 
  }, [currentDay]);

  const onFinish = values => {
    if (values.hasOwnProperty('courses')) {
      const coursesObj =
      values?.courses.length > 0 &&
        values?.courses.map(course => {
          let obj = null;
          currentCourseRef?.current && currentCourseRef?.current.map(cc => {
            if (course == cc?._id || course?._id == cc?._id) {
              obj = cc;
            }
          })
          return obj;
        }).filter(el => el !== null);
      values.courses = coursesObj || [];
    }

    result(values);
    AbsFormSickness.resetFields();
  };

  const options = [
    {
      key: 'day',
      value: 'day',
      label: (
        <>
          <span className="label__title">Une journée</span>
        </>
      ),
    },
    {
      key: 'half-day',
      value: 'half-day',
      label: (
        <>
          <span className="label__title">Une demi-journée</span>
        </>
      ),
    },
    {
      key: 'more',
      value: 'more',
      label: (
        <>
          <span className="label__title">Un ou plusieurs cours</span>
        </>
      ),
    },
  ];

  // Select courses by current child and current day of the week
  const selectCourses = useCallback(objSend => {
    // initial data for SelectFieldAxios component
    const newElementSelect = {
      label: 'Cours',
      name: 'courses',
      endponit: `students/courses/${getObjId(selectedStudent)}`,
      required: hasCourse,
      displayLabel: true,
      type: 'axios-select',
      tagRender: props => (
        <span className="multiple-label-list">{props.label}</span>
      ),
      itemCallback: item => {
        return {
          key: item?._id,
          title: `<span class="time">${moment(item?.course_time_start).format(
            'HH:mm',
          )}</span> <span class="cours-name">${item?.subject?.name}</span>`,
          label: `${item?.subject?.name}`,
          value: item?._id,
          color: item?.subject?.color,
          item: item,
        };
      },
    };

    return !!selectedStudent && (
      <>
        <SelectFieldAxios
          mode="multiple"
          isSearch={false}
          sortBy="title"
          className="axios-select"
          placeholder="Selectionner le(s) cours"
          tagRender={newElementSelect.tagRender}
          endponit={newElementSelect.endponit}
          endponitSend={objSend}
          getResponse={response => {
            if (response?.data.length > 0) {
              setHasCourse(true);
              setCurrentCourse(response?.data);
            } else {
              setHasCourse(false);
            }
          }}
          itemCallback={newElementSelect.itemCallback}
          item={newElementSelect}
        />
        {!hasCourse && (
          <span className="alert axios-alert">
            Il n'y a pas de cours à cette date.
          </span>
        )}
      </>
    );
  }, [selectedStudent, hasCourse, setCurrentCourse, setHasCourse]);

  // Select student for current absence
  const selectStudents = useMemo(() => {
    // initial data for SelectFieldAxios component
    const newElementSelect = {
      label: 'Étudiant',
      name: 'student',
      endponit: `students/list/select`,
      endponitSend: {},
      required: true,
      value: '',
      displayLabel: true,
      type: 'axios-select',
      tagRender: props => (
        <span className="multiple-label-list">{props.label}</span>
      ),
      onSelect: (LabeledValue, option) => {
        setSelectedStudent(option?.item);
        localUser?.role == 'parent' && (
          changeChildByID(option?.item?._id || option?.item)
        )
        const contact_person = option?.item?.contact_person?._id || option?.item?.contact_person;
        const parent = contact_person || option?.item?.parents[0]?._id || option?.item?.parents[0]
        setStudentParent(parent);
      },
      itemCallback: item => {
        return {
          key: item._id,
          label: `${item?.description?.lastname} ${
            item?.description?.firstname
          }`,
          value: item._id,
          item: item,
        };
      },
    };

    return (
      <>
        <SelectFieldAxios
          isSearch={true}
          sortBy="label"
          className="axios-select"
          placeholder="Sélectionnez un étudiant"
          tagRender={newElementSelect.tagRender}
          itemCallback={newElementSelect.itemCallback}
          item={newElementSelect}
          onSelect={newElementSelect.onSelect}
          endponit={newElementSelect.endponit}
          endponitSend={newElementSelect.endponitSend}
        />
        {studentParentRef?.current && (
          <Form.Item name="parent" hidden={true}
            initialValue={studentParentRef.current}
            rules={[
              {
                required: false,
                message: '',
              },
            ]}
          >
            <Input />
          </Form.Item>
        )}
      </>
    );
  }, [studentParentRef, selectedStudent, localUser]);


  return (
    <div className={childClassName}>
      <Form
        form={AbsFormSickness}
        className="form-absence__form"
        name="form_absence_sickness"
        initialValues={initFormData}
        onFinish={onFinish}
        onValuesChange={values => {
          values?.duration && setDurationType(values?.duration);
          setIsDisabled(false);
          values?.status && setAbsenceStatus(values?.status);
        }}
      >
        {localUser?.role == 'teacher' && selectStudents}

        <Form.Item name="duration" label="Durée">
          <Radio.Group
            className="radio-group"
            options={options}
            optionType="Radio"
          />
        </Form.Item>

        <CustomDate
          name="date"
          value={currentDay}
          onChange={val => {
            setCurrentDay(val);
            AbsFormSickness.setFieldsValue({
              courses: [],
            });
          }}
        />

        {durationType == 'more' ? selectCourses(endponitObj) : ''}

        {localUser?.role == 'teacher' && (
          <CustomSelect
            className={`accent-label select-status ${absenceStatus}`}
            label={`Status`}
            value={absenceStatus}
            placeholder="Selectionner"
            name={`status`}
            options={getStatusesList(formData?.type || 'sickness')}
          />
        )}

        <div className="absence-actions-wrapper">
          <Button
            className="btn-previous"
            onClick={() => prevStep !== undefined && prevStep()}
          >
            Précédent
          </Button>
          <Button
            className="confirm-btn"
            form="form_absence_sickness"
            htmlType="submit"
            disabled={isDisabled}
          >
            Confirmer
          </Button>
        </div>
      </Form>
    </div>
  );
}

SicknessForm.defaultProps = {};
SicknessForm.propTypes = {
  result: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
};

export default memo(SicknessForm);
